import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight, ShieldCheck, Zap } from "lucide-react";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { LS_LEAD } from "@/pages/PaymentSuccess";

export default function LeadDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setForm({ name: "", phone: "", email: "" });
    setLoading(false);
  };

  const handleClose = (val) => {
    if (!val) setTimeout(reset, 200);
    onOpenChange(val);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    if (form.phone.replace(/\D/g, "").length < 6) {
      toast.error("Please enter a valid phone number");
      return;
    }
    try {
      setLoading(true);
      const res = await createLead(form);
      // Save the lead id so /success can auto-verify after Razorpay redirects back
      localStorage.setItem(LS_LEAD, res.id);
      toast.success("Redirecting to secure payment...");
      // Redirect in the SAME tab so Razorpay's success redirect brings the user back here
      setTimeout(() => {
        window.location.href = res.payment_url;
      }, 600);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
        data-testid="lead-dialog"
      >
        <div className="bg-[#1E3A8A] px-6 pt-6 pb-8 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit" }}>
              Get Instant Access – ₹199
            </DialogTitle>
            <DialogDescription className="text-blue-100/90 mt-1">
              Enter your details, complete the ₹199 payment, and the PDF will download automatically.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4 bg-white">
          <div className="space-y-2">
            <Label htmlFor="lead-name" className="text-slate-700 font-medium">Full Name</Label>
            <Input
              id="lead-name" name="name" placeholder="Your name"
              value={form.name} onChange={handleChange}
              className="rounded-xl border-slate-200 h-12 text-base"
              data-testid="lead-name-input" required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lead-phone" className="text-slate-700 font-medium">WhatsApp Number</Label>
            <Input
              id="lead-phone" name="phone" type="tel" placeholder="+91 98765 43210"
              value={form.phone} onChange={handleChange}
              className="rounded-xl border-slate-200 h-12 text-base"
              data-testid="lead-phone-input" required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lead-email" className="text-slate-700 font-medium">
              Email <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Input
              id="lead-email" name="email" type="email" placeholder="you@example.com"
              value={form.email} onChange={handleChange}
              className="rounded-xl border-slate-200 h-12 text-base"
              data-testid="lead-email-input"
            />
          </div>

          <Button
            type="submit" disabled={loading}
            className="w-full h-14 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold text-base cta-glow transition-all hover:-translate-y-0.5"
            data-testid="lead-form-submit"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Redirecting...</>
            ) : (
              <>Pay ₹199 &amp; Get PDF <ArrowRight className="w-5 h-5 ml-2" /></>
            )}
          </Button>

          <div className="flex items-center justify-center gap-4 pt-2">
            <p className="flex items-center gap-1.5 text-xs text-slate-500">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              Secure Razorpay
            </p>
            <p className="flex items-center gap-1.5 text-xs text-slate-500">
              <Zap className="w-3.5 h-3.5 text-orange-500" />
              Auto download
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
